import type { Item } from '@hugo/core'
import { createContext, type ReactNode, useContext, useMemo, useState } from 'react'

/**
 * UI state shared across the shell: the selected item (lists, palette and toolbar act on it),
 * the item open in the details sheet, the capture dialog (typed or dictated) and the snooze
 * picker.
 */
type Workspace = {
  selected: Item | null
  setSelected: (item: Item | null) => void
  detailsId: string | null
  openDetails: (id: string | null) => void
  captureOpen: boolean
  setCaptureOpen: (open: boolean) => void
  /** Open the capture dialog and start dictating right away (V, the palette). */
  openDictation: () => void
  /** Set when the capture dialog should start dictating as it opens; it clears it. */
  dictateOnOpen: boolean
  clearDictateOnOpen: () => void
  snoozeOpen: boolean
  setSnoozeOpen: (open: boolean) => void
}

const WorkspaceContext = createContext<Workspace | null>(null)

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const [selected, setSelected] = useState<Item | null>(null)
  const [detailsId, openDetails] = useState<string | null>(null)
  const [captureOpen, setCaptureOpen] = useState(false)
  const [dictateOnOpen, setDictateOnOpen] = useState(false)
  const [snoozeOpen, setSnoozeOpen] = useState(false)
  const value = useMemo(
    () => ({
      selected,
      setSelected,
      detailsId,
      openDetails,
      captureOpen,
      setCaptureOpen,
      openDictation: () => {
        setDictateOnOpen(true)
        setCaptureOpen(true)
      },
      dictateOnOpen,
      clearDictateOnOpen: () => setDictateOnOpen(false),
      snoozeOpen,
      setSnoozeOpen,
    }),
    [selected, detailsId, captureOpen, dictateOnOpen, snoozeOpen],
  )
  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>
}

export function useWorkspace(): Workspace {
  const value = useContext(WorkspaceContext)
  if (!value) throw new Error('useWorkspace must be used inside <WorkspaceProvider>')
  return value
}
